/**
 * Cookie Consent Banner
 *
 * Implements state-based consent model for US customers:
 * - Opt-In states (CA, CT, VA): Tracking blocked until explicit consent
 * - Opt-Out states (other US): Tracking loads by default, can reject
 *
 * Manages: GTM, Google Analytics, Meta/Facebook Pixel, Klaviyo
 */

(function() {
  'use strict';

  var CookieConsent = {
    storageKey: 'ts-cookie-consent',
    stateKey: 'ts-visitor-state',

    // Opt-in states: tracking blocked by default until explicit consent
    optInStates: ['CA', 'CT', 'VA'],

    consent: null,
    visitorState: null,
    isOptInState: false,
    bannerElement: null,

    /**
     * Initialize cookie consent
     */
    init: function() {
      // Check for force-show param (for testing): ?consent=show
      var urlParams = new URLSearchParams(window.location.search);
      var forceShow = urlParams.get('consent') === 'show';

      if (forceShow) {
        // Clear existing consent to force banner display
        localStorage.removeItem(this.storageKey);
      }

      // Only run for US market (unless forcing)
      if (!forceShow && (!window.theme || !window.theme.settings || window.theme.settings.market !== 'US')) {
        // Non-US: Load tracking immediately
        this.loadTracking();
        return;
      }

      // Check existing consent
      this.consent = forceShow ? null : localStorage.getItem(this.storageKey);

      // Detect visitor state
      this.detectState().then(function(state) {
        CookieConsent.visitorState = state;
        CookieConsent.isOptInState = CookieConsent.optInStates.indexOf(state) !== -1;

        // Cache state for future visits
        if (state) {
          localStorage.setItem(CookieConsent.stateKey, state);
        }

        CookieConsent.handleConsent();
      });
    },

    /**
     * Detect visitor's US state via Shopify's browsing context API
     */
    detectState: function() {
      // Check cached state first
      var cachedState = localStorage.getItem(this.stateKey);
      if (cachedState) {
        return Promise.resolve(cachedState);
      }

      // Fetch from Shopify
      return fetch('/browsing_context_suggestions.json')
        .then(function(response) {
          return response.json();
        })
        .then(function(data) {
          // Shopify returns province_code for US states (e.g., "CA", "TX")
          if (data && data.detected_values && data.detected_values.province_code) {
            return data.detected_values.province_code;
          }
          return null;
        })
        .catch(function() {
          // Default to opt-out behavior if detection fails
          return null;
        });
    },

    /**
     * Handle consent based on state and prior decision
     */
    handleConsent: function() {
      if (!this.consent) {
        // First visit - show banner
        this.showBanner();

        // For opt-out states, load tracking immediately (can reject later)
        if (!this.isOptInState) {
          this.loadTracking();
        }
      } else if (this.consent === 'accepted') {
        // Returning visitor who accepted
        this.loadTracking();
      }
      // If rejected, do nothing (no tracking)
    },

    /**
     * User accepts cookies
     */
    accept: function() {
      localStorage.setItem(this.storageKey, 'accepted');
      this.consent = 'accepted';
      this.loadTracking();
      this.hideBanner();
    },

    /**
     * User rejects cookies
     */
    reject: function() {
      localStorage.setItem(this.storageKey, 'rejected');
      this.consent = 'rejected';
      this.disableTracking();
      this.hideBanner();
    },

    /**
     * Show privacy choices modal (optional - can link to privacy policy)
     */
    showChoices: function() {
      // For now, redirect to privacy policy page
      window.location.href = '/policies/privacy-policy';
    },

    /**
     * Load tracking scripts
     */
    loadTracking: function() {
      // Dispatch event for deferred tracking scripts
      window.dispatchEvent(new CustomEvent('cookie-consent-accepted'));

      // Call the deferred tracking loader if available
      if (typeof window.loadTrackingScripts === 'function') {
        window.loadTrackingScripts();
      }
    },

    /**
     * Disable tracking (for opt-out states that already loaded)
     */
    disableTracking: function() {
      // Dispatch event to signal tracking should stop
      window.dispatchEvent(new CustomEvent('cookie-consent-rejected'));

      // Disable Google Analytics
      if (window['ga-disable-UA-XXXXXXXX-X'] !== undefined) {
        window['ga-disable-UA-XXXXXXXX-X'] = true;
      }

      // Disable Meta Pixel
      if (typeof fbq === 'function') {
        // Revoke consent for Meta Pixel
        fbq('consent', 'revoke');
      }

      // Note: GTM and Klaviyo don't have simple disable mechanisms
      // They should be conditionally loaded in theme.liquid
    },

    /**
     * Show the cookie consent banner
     */
    showBanner: function() {
      this.bannerElement = document.getElementById('cookie-consent-banner');
      if (this.bannerElement) {
        this.bannerElement.style.display = 'flex';
      }
    },

    /**
     * Hide the cookie consent banner
     */
    hideBanner: function() {
      if (this.bannerElement) {
        this.bannerElement.style.display = 'none';
      }
    }
  };

  // Expose globally
  window.CookieConsent = CookieConsent;

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      CookieConsent.init();
    });
  } else {
    CookieConsent.init();
  }
})();
